import React from 'react';
import Card from 'react-bootstrap/Card';
import Col from 'react-bootstrap/Col';
import { Tooltip } from '@mui/material';
import { Autorenew, Upload, Download, Error } from '@mui/icons-material';
import CustomTable from '../../../components/table/table';

export default function SyncLogsTable({
    selectedConnection,
    getPlatformDisplayName,
    refreshingLogs,
    fetchLogs,
    logsCurrentPage,
    logsPerPage,
    logsSearchQuery,
    logsSortBy,
    logs,
    logsTotalCount
}) {
    const logColumns = [
        {
            field: 'action',
            title: 'Action',
            class: 'w-10-percent',
            render: (row) => {
                const isPush = row.action === 'PUSH';
                return (
                    <span
                        className={`badge ${isPush ? 'badge-push' : 'badge-pull'} text-uppercase d-inline-flex align-items-center`}
                        style={{ whiteSpace: 'nowrap', padding: '0.35em 0.75em' }}
                    >
                        {isPush ? (
                            <Upload style={{ fontSize: '0.8rem', marginRight: '4px' }} />
                        ) : (
                            <Download style={{ fontSize: '0.8rem', marginRight: '4px' }} />
                        )}
                        {row.action}
                    </span>
                );
            }
        },
        {
            field: 'entity_type',
            title: 'Entity',
            class: 'w-12-percent',
            render: (row) => (
                <span className="text-uppercase small fw-bold" style={{ whiteSpace: 'nowrap' }}>
                    {row.entity_type || '—'}
                </span>
            )
        },
        {
            field: 'entity_name',
            title: 'Ref / Details',
            class: 'w-24-percent',
            render: (row) => {
                const name = row.entity_name || '—';
                return (
                    <div className="text-truncate font-weight-bold" style={{ maxWidth: '100%' }} title={name}>
                        {name}
                    </div>
                );
            }
        },
        {
            field: 'status',
            title: 'Status',
            class: 'w-12-percent',
            render: (row) => (
                <span className={`badge ${row.status === 'success' ? 'bg-success' : row.status === 'failed' ? 'bg-danger' : 'bg-warning text-dark'} text-uppercase`} style={{ whiteSpace: 'nowrap', padding: '0.35em 0.65em' }}>
                    {row.status}
                </span>
            )
        },
        {
            title: 'Error Details',
            field: 'error_message',
            class: 'w-13-percent',
            render: (row) => {
                if (row.status === 'failed' && row.error_message) {
                    return (
                        <Tooltip title={row.error_message} arrow placement="top">
                            <span className="d-inline-flex align-items-center text-danger" style={{ gap: '6px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                                <Error style={{ fontSize: '1.15rem' }} />
                                <span className="small text-decoration-underline" style={{ fontWeight: '500' }}>View Error</span>
                            </span>
                        </Tooltip>
                    );
                }
                return <span className="text-muted">—</span>;
            }
        },
        {
            title: 'Triggered By',
            field: 'triggered_by',
            class: 'w-14-percent',
            render: (row) => {
                const name = row.user ? [row.user.first_name, row.user.last_name].filter(Boolean).join(' ') : 'System';
                return <span style={{ whiteSpace: 'nowrap' }}>{name}</span>;
            }
        },
        {
            field: 'createdAt',
            title: 'Datetime',
            class: 'w-datetime',
            render: (row) => {
                const d = new Date(row.createdAt);
                const day = String(d.getDate()).padStart(2, '0');
                const month = String(d.getMonth() + 1).padStart(2, '0');
                const year = d.getFullYear();
                let hours = d.getHours();
                const minutes = String(d.getMinutes()).padStart(2, '0');
                const seconds = String(d.getSeconds()).padStart(2, '0');
                const ampm = hours >= 12 ? 'pm' : 'am';
                hours = hours % 12;
                hours = hours ? hours : 12;
                const hourStr = String(hours).padStart(2, '0');
                return (
                    <span style={{ whiteSpace: 'nowrap', fontFamily: 'monospace', minWidth: '210px', display: 'inline-block' }}>
                        {`${day}-${month}-${year} ${hourStr}:${minutes}:${seconds} ${ampm}`}
                    </span>
                );
            }
        }
    ];

    return (
        <Col lg={12} className="mb-4">
            <Card className="shadow-sm border-0 mb-3 h-100">
                <Card.Header className="bg-transparent border-0 pt-4 px-4 pb-0 d-flex justify-content-between align-items-center">
                    <Card.Title as="h2" className="content-heading content-heading-lg text-wrap text-uppercase m-0 brand-heading">
                        Sync Logs - {getPlatformDisplayName(selectedConnection.platform)} ({selectedConnection.display_name || selectedConnection.platform_connection_name})
                    </Card.Title>
                    <Tooltip title="Refresh Logs">
                        <span>
                            <button
                                data-cy="refresh-logs-button"
                                onClick={() => fetchLogs(selectedConnection.id, logsCurrentPage, logsPerPage, logsSearchQuery, logsSortBy)}
                                aria-label="refresh"
                                className="btn p-0 border-0"
                                style={refreshingLogs ? { animation: 'spin 1.5s linear infinite', marginRight: '5px' } : { marginRight: '5px' }}
                                disabled={refreshingLogs}
                            >
                                <Autorenew className="brand-icon" />
                            </button>
                        </span>
                    </Tooltip>
                </Card.Header>
                <Card.Body className="p-4">
                    <div className="sync-logs-table-container">
                        <CustomTable
                            columns={logColumns}
                            data={logs}
                            toolbar={true}
                            itemsPerPageCustom={true}
                            itemsPerPage={logsPerPage}
                            currentPage={logsCurrentPage}
                            paginationCount={logsTotalCount}
                            pageHandler={(page, limit) => fetchLogs(selectedConnection.id, page, limit, logsSearchQuery, logsSortBy)}
                            limitChangeHandler={(limit) => fetchLogs(selectedConnection.id, 1, limit, logsSearchQuery, logsSortBy)}
                            searchHandler={(search) => fetchLogs(selectedConnection.id, 1, logsPerPage, search, logsSortBy)}
                            externalSearchValue={logsSearchQuery}
                            externalSearchHandler={(search) => fetchLogs(selectedConnection.id, 1, logsPerPage, search, logsSortBy)}
                            sortBy={logsSortBy}
                            sortHandler={(sortBy) => fetchLogs(selectedConnection.id, 1, logsPerPage, logsSearchQuery, sortBy)}
                            isLoading={refreshingLogs}
                            cookieName="recordsPerPage_sync_logs"
                            exportButton={false}
                            addButton={false}
                            isEdit={false}
                            isDelete={false}
                            isMore={false}
                            additionalActions={[]}
                            fileName={`sync_logs_${selectedConnection.platform}_${new Date().toISOString().slice(0, 10)}`}
                        />
                    </div>
                </Card.Body>
            </Card>
        </Col>
    );
}
